/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: Fullscreen interactive sandbox to test component props, states, and themes
 * Style referenceFiles:
 * Design for: Desktop
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - Live preview of component
 * - Toggle Theme (Light/Dark)
 * - Change state (Hover, Active, Disabled)
 * - Adjust Props dynamically
 * - Adjust viewport size
 * - Navigate to: ComponentDetail (Back)
 * </page-design>
 */

import { useState } from 'react'
import { useAtomValue } from 'jotai'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import * as ComponentDetailStore from '@/stores/ComponentDetailStore'
import { pageLinks } from '@/pageLinks'

const viewportPresets = [
  { label: '手机', width: 375, icon: 'lucide:smartphone' },
  { label: '平板', width: 768, icon: 'lucide:tablet' },
  { label: '桌面', width: 1280, icon: 'lucide:monitor' },
]

const interactionStates = ['默认', '悬停', '聚焦', '激活', '加载中', '禁用']

export default function ComponentPreview() {
  const { data, isPending } = useAtomValue(ComponentDetailStore.refreshAtom)
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [activeState, setActiveState] = useState('默认')
  const [viewport, setViewport] = useState(1280)
  const [variant, setVariant] = useState('primary')
  const [size, setSize] = useState('md')
  const [label, setLabel] = useState('点击操作')

  if (isPending || !data) {
    return <div className="flex h-screen items-center justify-center bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25]"><div className="animate-pulse text-gray-400">加载中...</div></div>
  }

  const comp = data.component
  const bgColor = theme === 'dark' ? 'bg-gray-900' : 'bg-white'
  const checkered = theme === 'dark' ? '' : 'bg-[image:linear-gradient(45deg,#f0f0f0_25%,transparent_25%,transparent_75%,#f0f0f0_75%,#f0f0f0),linear-gradient(45deg,#f0f0f0_25%,transparent_25%,transparent_75%,#f0f0f0_75%,#f0f0f0)] bg-[size:20px_20px] bg-[position:0_0,10px_10px]'

  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25] overflow-hidden">
      {/* Main Canvas */}
      <div className="flex-1 flex flex-col h-full">
        <header className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Link to={pageLinks.ComponentDetail(comp.id)} className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-600 shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all"><Icon icon="lucide:arrow-left" className="text-xl" /></Link>
            <h1 className="text-lg font-bold">{comp.name}</h1>
            <span className="text-xs text-gray-500">交互预览</span>
          </div>
          <div className="flex items-center gap-2">
            {viewportPresets.map(v => (
              <button key={v.width} onClick={() => setViewport(v.width)} className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-all ${viewport === v.width ? 'bg-[#1D263A]/5 text-[#1D263A] font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
                <Icon icon={v.icon} className="text-lg" />{v.label}
              </button>
            ))}
          </div>
        </header>

        <div className={`flex-1 flex items-center justify-center p-8 ${checkered}`}>
          <div className={`${bgColor} rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] flex items-center justify-center transition-all duration-300 overflow-hidden`} style={{ width: Math.min(viewport, 1200), height: 400 }}>
            <div className="text-center">
              {/* Mock rendered component */}
              <button
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  variant === 'primary' ? 'bg-[#1D263A] text-white shadow-[0_4px_12px_rgba(29,38,58,0.25)]' :
                  variant === 'secondary' ? 'bg-gray-100 text-gray-700' :
                  'bg-red-600 text-white shadow-[0_4px_12px_rgba(239,68,68,0.25)]'
                } ${size === 'sm' ? 'text-sm px-4 py-2' : size === 'lg' ? 'text-lg px-8 py-4' : ''} ${
                  activeState === '禁用' ? 'opacity-50 cursor-not-allowed' :
                  activeState === '加载中' ? 'opacity-70' :
                  activeState === '悬停' ? 'scale-105' :
                  activeState === '聚焦' ? 'ring-2 ring-[#1D263A]/30 ring-offset-2' : ''
                }`}
                disabled={activeState === '禁用'}
              >
                {activeState === '加载中' ? '加载中...' : label}
              </button>
              <p className={`mt-4 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {variant} / {size} / {activeState}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Control Panel */}
      <div className="w-80 bg-white border-l border-gray-100 flex flex-col h-full overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h2 className="font-bold">控制面板</h2>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-gray-500 mb-3">主题</h3>
            <div className="flex gap-2">
              {(['light', 'dark'] as const).map(t => (
                <button key={t} onClick={() => setTheme(t)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${theme === t ? 'bg-[#1D263A]/5 text-[#1D263A]' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>
                  {t === 'light' ? '亮色' : '暗色'}
                </button>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-500 mb-3">交互状态</h3>
            <div className="flex flex-wrap gap-2">
              {interactionStates.map(s => (
                <button key={s} onClick={() => setActiveState(s)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeState === s ? 'bg-[#1D263A]/5 text-[#1D263A]' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>{s}</button>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-500 mb-3">Props</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">variant</label>
                <select value={variant} onChange={e => setVariant(e.target.value)} className="w-full px-3 py-2 bg-gray-50 rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15">
                  <option value="primary">primary</option>
                  <option value="secondary">secondary</option>
                  <option value="danger">danger</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">size</label>
                <select value={size} onChange={e => setSize(e.target.value)} className="w-full px-3 py-2 bg-gray-50 rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15">
                  <option value="sm">sm</option>
                  <option value="md">md</option>
                  <option value="lg">lg</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">label</label>
                <input type="text" value={label} onChange={e => setLabel(e.target.value)} className="w-full px-3 py-2 bg-gray-50 rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15" />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
